// ---------- la couverture d'un pack : sa catégorie et ses techniques, jamais son sujet ----------
// La vitrine des packs (2026-10-23, src/packs.ts) avait besoin d'images : une page de 33 cartes
// de texte ne donnait envie de rien. Mais la règle du catalogue tient toujours — on découvre le
// sujet en coloriant (cf. buildCard dans home.ts) — donc ni la vignette d'un puzzle, ni même une
// version floutée : un flou de visage se reconnaît.
//
// Ce qui reste, et qui suffit : la TEINTE de la catégorie (orangé pour les animaux, bleu nuit
// pour les monuments…) et une feuille quadrillée posée de biais, chaque bande dans la TEXTURE
// d'une technique du pack — des points pour le pointillisme, des croix pour la broderie, des
// briques… Le joueur voit « à quoi ça ressemblera », pas « ce que c'est ».
//
// Dessinée au canevas, sans aucun asset : hors ligne, sans requête, et DÉTERMINISTE par pack (le
// hasard est semé par son identifiant), pour qu'une couverture ne change pas d'une ouverture à
// l'autre.

import { techniqueFamily, type TechFamily } from './techniqueIcon';
import { PIGMENTS as POINTILLE_PIGMENTS } from '../shared/alphabets/pointille';
import { PAINTS, stroke, toneOf, type Tone } from '../shared/alphabets/peinture';

/** La teinte de chaque catégorie, en degrés HSL : celle de sa pastille dans la Bibliothèque
 *  (.home-tag.cat-* dans style.css, sept angles pour douze catégories, appariées de la même façon).
 *  Choisies d'abord à part, elles contredisaient la Bibliothèque — les animaux orange ici, violets
 *  là-bas — et donnaient la même à l'art et aux personnes (revue du 2026-10-23). Une catégorie
 *  inconnue prend celle de la Sélection, qui n'a pas de pastille. */
const HUE: Record<string, number> = {
  celebrite: 332,
  paysage: 166,
  monuments: 166,
  animaux: 254,
  contes: 254,
  vehicules: 212,
  sport: 32,
  saisonnier: 32,
  art: 135,
  personnes: 291,
  scenes: 291,
  divers: 240,
};

export interface CoverSource {
  id: string;
  category: string | null;
  alphabets: ReadonlyArray<{ alphabetId: string; count: number }>;
}

/** Un générateur pseudo-aléatoire semé par une chaîne : la même couverture à chaque fois. */
function seeded(seed: string): () => number {
  let s = 0;
  for (const ch of seed) s = (s * 31 + ch.charCodeAt(0)) >>> 0;
  return () => (s = (s * 1664525 + 1013904223) >>> 0) / 4294967296;
}

/** Les pigments du pointillisme du jeu : une couverture évoque la technique, avec ses vraies
 *  couleurs plutôt qu'une palette inventée à côté. */
const PIGMENTS = POINTILLE_PIGMENTS.map((p) => p.css);
const GRAPHITE = '#1c1830';

/** Une case de la feuille, dans la texture d'une famille de techniques. Pas le vrai dessin de
 *  l'alphabet — une évocation à quelques traits, lisible à 15 px. */
/**
 * LA PEINTURE, d'après la vraie touche (2026-10-23). Elle était un trait rond d'une seule teinte,
 * penché au hasard, case par case : « pas très ressemblant ». Ce qui fait une toile du jeu, ce
 * sont trois choses, et les trois sont reprises ici :
 *  - la TOUCHE elle-même, `stroke()` de peinture.ts — forme effilée, dégradé, poils, tremblé ;
 *  - un sens qui SUIT LES FORMES : dans le jeu l'angle vient du gradient de l'image
 *    (strokeAngles), ici d'un champ continu, pour que les voisines se couchent ensemble ;
 *  - des PLAGES de couleurs voisines, prises dans le vrai nuancier, qui glissent du clair au
 *    sombre comme le ciel d'un paysage ou le modelé d'un visage, au lieu d'une teinte par case.
 * La palette est une « scène » tirée par pack : la couverture ne doit rien dire du sujet, mais
 * quatre ambiances suffisent à ce que deux couvertures voisines ne soient pas jumelles.
 */
const PAINT_SCENES: readonly (readonly string[])[] = [
  // ciel et prairie
  ['blanc bleuté', 'bleu ciel', 'gris vert', 'vert', 'vert sombre'],
  // portrait, terres chaudes
  ['chair claire', 'chair', 'terre de Sienne brûlée', 'bordeaux', 'terre d’ombre'],
  // nocturne
  ['gris bleu', 'jaune', 'bleu', 'bleu nuit', 'noir d’ivoire'],
  // bouquet
  ['blanc', 'jaune', 'orange', 'rose', 'vert sombre'],
].map((names) => names.map((n) => PAINTS.find((p) => p.name === n)!.css));

/** L'état d'une couverture peinte : posé par paintCover() avant la boucle des cases. */
let paint: { tones: Tone[]; a: number; b: number; c: number; base: number } | null = null;

function paintCell(g: CanvasRenderingContext2D, x: number, y: number, s: number, R: () => number) {
  if (!paint) return;
  const q = x / s;
  const r = y / s;
  // La clarté glisse du haut vers le bas, ondulée : des plages, pas un dégradé de manuel.
  const v =
    0.5 +
    0.22 * Math.sin(q * 0.42 + paint.a) * Math.cos(r * 0.35 + paint.b) +
    0.06 * r +
    (R() - 0.5) * 0.28;
  const k = Math.max(0, Math.min(paint.tones.length - 1, Math.floor(v * paint.tones.length)));
  const angle = paint.base + 0.7 * Math.sin(q * 0.3 + r * 0.22 + paint.c);
  stroke(g, x, y, s, s, paint.tones[k], angle);
}

function cell(
  g: CanvasRenderingContext2D,
  fam: TechFamily,
  x: number,
  y: number,
  s: number,
  R: () => number,
  hue: number,
): void {
  const pick = () => PIGMENTS[Math.floor(R() * PIGMENTS.length)];
  const tint = (spread: number, l0: number, l1: number) =>
    `hsl(${(hue + R() * spread - spread / 2 + 360) % 360} 60% ${l0 + R() * (l1 - l0)}%)`;
  g.save();
  switch (fam) {
    case 'pointille': {
      g.fillStyle = pick();
      g.beginPath();
      g.arc(x + s / 2, y + s / 2, s * (0.18 + R() * 0.24), 0, 7);
      g.fill();
      break;
    }
    case 'motifs': {
      g.fillStyle = GRAPHITE;
      const k = Math.floor(R() * 5);
      if (k === 0) g.fillRect(x, y + s * 0.5, s, s * 0.5);
      else if (k === 1) {
        g.beginPath();
        g.moveTo(x + s, y);
        g.lineTo(x + s, y + s);
        g.lineTo(x, y + s);
        g.fill();
      } else if (k === 2) g.fillRect(x, y, s * 0.5, s);
      else if (k === 3) g.fillRect(x + s * 0.4, y, s * 0.2, s);
      break;
    }
    case 'hachures': {
      g.strokeStyle = GRAPHITE;
      g.lineWidth = s * 0.1;
      g.beginPath();
      g.rect(x, y, s, s);
      g.clip();
      g.beginPath();
      const n = 1 + Math.floor(R() * 3);
      for (let i = 0; i < n; i++) {
        const o = ((i + 1) / (n + 1)) * s * 2;
        g.moveTo(x + o - s, y + s);
        g.lineTo(x + o, y);
      }
      g.stroke();
      break;
    }
    case 'des': {
      g.fillStyle = '#ffffff';
      g.fillRect(x + s * 0.08, y + s * 0.08, s * 0.84, s * 0.84);
      g.fillStyle = GRAPHITE;
      const P = [
        [0.5, 0.5],
        [0.28, 0.28],
        [0.72, 0.72],
        [0.72, 0.28],
        [0.28, 0.72],
        [0.28, 0.5],
        [0.72, 0.5],
      ];
      const faces = [[0], [1, 2], [1, 0, 2], [1, 2, 3, 4], [1, 2, 3, 4, 0], [1, 2, 3, 4, 5, 6]];
      for (const i of faces[Math.floor(R() * 6)]) {
        g.beginPath();
        g.arc(x + P[i][0] * s, y + P[i][1] * s, s * 0.08, 0, 7);
        g.fill();
      }
      break;
    }
    case 'ascii': {
      g.fillStyle = GRAPHITE;
      g.font = `700 ${s * 0.8}px ui-monospace, monospace`;
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      g.fillText('.:+*#@'[Math.floor(R() * 6)], x + s / 2, y + s / 2);
      break;
    }
    case 'briques': {
      g.fillStyle = tint(40, 45, 70);
      g.fillRect(x + s * 0.06, y + s * 0.12, s * 0.88, s * 0.76);
      g.fillStyle = 'rgba(255,255,255,.35)';
      g.fillRect(x + s * 0.06, y + s * 0.12, s * 0.88, s * 0.12);
      break;
    }
    case 'peinture':
      paintCell(g, x, y, s, R);
      break;
    case 'croix': {
      g.strokeStyle = tint(50, 40, 65);
      g.lineWidth = s * 0.26;
      g.lineCap = 'round';
      g.beginPath();
      g.moveTo(x + s * 0.18, y + s * 0.18);
      g.lineTo(x + s * 0.82, y + s * 0.82);
      g.moveTo(x + s * 0.82, y + s * 0.18);
      g.lineTo(x + s * 0.18, y + s * 0.82);
      g.stroke();
      break;
    }
  }
  g.restore();
}

/** Les familles du pack, la plus fournie d'abord : la première bande de la feuille est la sienne. */
function families(p: CoverSource): TechFamily[] {
  const n = new Map<TechFamily, number>();
  for (const a of p.alphabets)
    n.set(techniqueFamily(a.alphabetId), (n.get(techniqueFamily(a.alphabetId)) ?? 0) + a.count);
  const list = [...n].sort((a, b) => b[1] - a[1]).map(([f]) => f);
  return list.length ? list : ['motifs'];
}

/**
 * Peint la couverture d'un pack dans `cv`, à la taille où le canevas s'affiche. `cells` : combien
 * de cases tiennent sur le petit côté — moins pour une vignette d'étagère, où chaque case doit
 * rester lisible, plus pour la bannière.
 *
 * `bleed` : la feuille couvre TOUT le cadre. Sur une tuile de pack, le fond teinté qui dépasse
 * dans un coin fait partie du dessin (une page posée sur la table) ; sur une carte de puzzle, où
 * l'aperçu prend toute la carte, ce même liseré se lisait comme un « petit espace » entre la
 * carte et l'illustration (retour du 2026-10-23).
 */
export function paintCover(cv: HTMLCanvasElement, p: CoverSource, cells = 7, bleed = false): void {
  const w = cv.clientWidth;
  const h = cv.clientHeight;
  if (!w || !h) return;
  const dpr = Math.min(2.5, window.devicePixelRatio || 1);
  cv.width = Math.round(w * dpr);
  cv.height = Math.round(h * dpr);
  const g = cv.getContext('2d')!;
  g.setTransform(dpr, 0, 0, dpr, 0, 0);
  const hue = HUE[p.category ?? 'divers'] ?? HUE.divers;
  const R = seeded(p.id);
  const bg = g.createLinearGradient(0, 0, w, h);
  bg.addColorStop(0, `hsl(${hue} 70% 88%)`);
  bg.addColorStop(1, `hsl(${(hue + 30) % 360} 60% 76%)`);
  g.fillStyle = bg;
  g.fillRect(0, 0, w, h);

  // La feuille, posée de biais et débordant du cadre : on en voit un morceau, comme d'une page
  // qu'on n'a pas encore retournée.
  const fams = families(p);
  // Une toile du jeu compte une centaine de touches par côté : à sept par vignette, elles
  // tenaient de la palette de peintre plus que du tableau. Le double, et le plancher plus bas —
  // une touche reste lisible à 6 px, là où un dé ou une croix ne l'est plus.
  const dense = fams[0] === 'peinture';
  const s = dense
    ? Math.max(6, Math.round(Math.min(w, h) / (cells * 2)))
    : Math.max(9, Math.round(Math.min(w, h) / cells));
  if (fams.includes('peinture')) {
    const scene = PAINT_SCENES[Math.floor(R() * PAINT_SCENES.length)];
    paint = {
      tones: scene.map(toneOf),
      a: R() * 6.28,
      b: R() * 6.28,
      c: R() * 6.28,
      base: (R() - 0.5) * 1.2,
    };
  }
  g.save();
  g.translate(w * 0.5, bleed ? h * 0.5 : h * 0.56);
  g.rotate(-0.12);
  // Pour couvrir le cadre une fois tournée de 0,12 rad, la feuille doit mesurer w·cos + h·sin
  // ≈ 1,12 w de large (et autant en hauteur) : 1,3 laisse la marge des cases arrondies.
  const cols = Math.ceil((w * 1.3) / s);
  const rows = Math.ceil((h * (bleed ? 1.3 : 1.1)) / s);
  const ox = (-cols * s) / 2;
  const oy = (-rows * s) / 2;
  g.fillStyle = '#fbf6ec';
  g.shadowColor = 'rgba(44,35,80,.25)';
  g.shadowBlur = 14;
  g.fillRect(ox, oy, cols * s, rows * s);
  g.shadowBlur = 0;
  for (let r = 0; r < rows; r++)
    for (let q = 0; q < cols; q++) {
      // Des bandes en biais, une par famille ; quelques cases laissées blanches, comme une
      // feuille en cours.
      const band = Math.min(
        fams.length - 1,
        Math.floor((q + r * 0.6) / ((cols * 1.6) / fams.length)),
      );
      // La peinture laisse moins de trous : ses touches débordent sur les voisines, et une toile
      // mitée de papier ne ressemblait plus à une toile.
      if (R() < (fams[Math.max(0, band)] === 'peinture' ? 0.05 : 0.18)) continue;
      cell(g, fams[Math.max(0, band)], ox + q * s, oy + r * s, s, R, hue);
    }
  g.strokeStyle = 'rgba(44,35,80,.08)';
  g.lineWidth = 1;
  g.beginPath();
  for (let q = 0; q <= cols; q++) {
    g.moveTo(ox + q * s, oy);
    g.lineTo(ox + q * s, oy + rows * s);
  }
  for (let r = 0; r <= rows; r++) {
    g.moveTo(ox, oy + r * s);
    g.lineTo(ox + cols * s, oy + r * s);
  }
  g.stroke();
  g.restore();
  // Le voile du bas, sur lequel se posent le pictogramme et, dans la bannière, le texte.
  const v = g.createLinearGradient(0, h * 0.45, 0, h);
  v.addColorStop(0, `hsla(${hue} 60% 40% / 0)`);
  v.addColorStop(1, `hsla(${hue} 55% 30% / .55)`);
  g.fillStyle = v;
  g.fillRect(0, 0, w, h);
}
